import {AsyncPipe, Location} from '@angular/common';
import {DialogService} from '../../services/dialog.service';
import {ActivatedRoute, Router} from '@angular/router';
import {CanComponentDeactivate} from '../../guards/can-deactive.guard';
import {ProgressComponent} from '../progress/progress.component';
import {UsersService} from '../../services/users.service';
import {BehaviorSubject, mergeMap, Observable, Subscription, throwError as observableThrowError} from 'rxjs';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {catchError, take} from 'rxjs/operators';
import {User} from '../../models/user';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BreadCrumb} from '../../models/breadcrumb';
import {collection, collectionData, DocumentData, Firestore, orderBy, query, where} from '@angular/fire/firestore';
import {Auth, authState} from '@angular/fire/auth';
import {MatError, MatFormField, MatInput} from '@angular/material/input';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {MatOption, MatSelect} from '@angular/material/select';

@Component({
    templateUrl: './users.form.component.html',
    imports: [
        MatError,
        ProgressComponent,
        BreadcrumbsComponent,
        MatCard,
        MatCardTitle,
        MatCardHeader,
        MatCardContent,
        MatFormField,
        ReactiveFormsModule,
        MatInput,
        MatSelect,
        MatOption,
        MatButton,
        AsyncPipe
    ],
    standalone: true
})
export class UsersFormComponent implements OnInit, OnDestroy, CanComponentDeactivate {

    public globalErrorMessage = '';
    accounts: DocumentData[];
    userId: string;
    user: User;
    accountId: string;
    isAdmin: boolean;
    authStateSubscription: Subscription;
    accountsSubscription: Subscription;
    facilitiesSubscription: Subscription;
    breadcrumbs: BreadCrumb[] = [
        {url: ['/'], title: 'Accounts'},
        {url: ['/users'], title: 'Users'},
        {url: null, title: 'New User'},
    ];
    public facilities: BehaviorSubject<DocumentData[]> = new BehaviorSubject([]);
    roles = [{name: 'Admin', value: 1}, {name: 'Operator-Employee', value: 2}, {name: 'Customer User', value: 3}];
    userForm: UntypedFormGroup;
    showFacilities = false;
    @ViewChild(ProgressComponent, {static: true}) progress: ProgressComponent;

    constructor(private activatedRoute: ActivatedRoute,
                private userService: UsersService,
                private location: Location,
                private firestore: Firestore,
                private dialogService: DialogService,
                private formBuilder: UntypedFormBuilder,
                private router: Router,
                private snackbar: MatSnackBar,
                private fireAuth: Auth) {
    }

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.userForm.dirty) {
            const confirm: Observable<boolean> = this.dialogService.confirm('Discard changes for User?');
            confirm.subscribe(conf => {
                if (!conf) {
                    this.location.go(this.router.url);
                    return false;
                }
            });
            return confirm;
        }
        return true;
    }

    ngOnInit() {
        this.userForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required, Validators.minLength(6)]],
            accountId: ['', Validators.required],
            role: ['', Validators.required],
            facilities: [''],
        });

        this.activatedRoute.paramMap.subscribe(
            params => {
                this.userId = params.get('userId');
                this.accountId = params.get('accountId');
                if (this.accountId) {
                    this.breadcrumbs[1].url = ['/account-users', this.accountId];
                }
                this.authStateSubscription = authState(this.fireAuth).pipe(
                    take(1),
                    mergeMap((user) => {
                        // We don't refresh the token here because it will get called often
                        return user.getIdTokenResult();
                    }),
                ).subscribe(token => {
                    this.isAdmin = !!token.claims.admin;
                    if (!this.isAdmin && this.roles.length === 3) {
                        this.roles = this.roles.slice(2);
                    }
                    if (this.userId) {
                        this.userForm.get('password').clearValidators();
                        this.userForm.get('password').clearAsyncValidators();
                        this.userForm.get('password').updateValueAndValidity();
                        this.userForm.updateValueAndValidity();
                        this.fetchUser(this.userId);
                    }
                });
            }
        );

        const accountsColRef = collection(this.firestore, 'accounts');
        const accountsQuery = query(accountsColRef, orderBy('name', 'asc'));

        this.accountsSubscription = collectionData(
            accountsQuery,
            {idField: 'id'}
        ).subscribe(data => {
            this.accounts = data;
        });

    }

    accountChanged(event: any) {
        const facilitiesColRef = collection(this.firestore, 'facilities');
        const facilitiesQuery = query(
            facilitiesColRef,
            where('accountId', '==', event.value),
            orderBy('name', 'asc')
        );
        this.facilitiesSubscription = collectionData(
            facilitiesQuery,
            {idField: 'id'}
        ).subscribe(data => {
            this.facilities.next(data);
        });
    }


    roleChanged(event) {
        this.showFacilities = event.value === 3;
    }

    save() {
        if (this.userForm.valid) {
            this.progress.setVisible(true);
            const user = {
                email: this.userForm.get('email').value,
                password: this.userForm.get('password').value,
                accountId: this.userForm.get('accountId').value,
                facilities: this.userForm.get('facilities').value,
                admin: false,
                operator: false,
                user: false,
            };
            const role = this.userForm.get('role').value;

            if (role === 1) {
                user.admin = true;
                user.operator = true;
                user.user = true;
            } else if (role === 2) {
                user.operator = true;
                user.user = true;
            } else if (role === 3) {
                user.user = true;
            }
            if (this.userId) {
                this.userService.updateUser(user, this.userId).pipe(
                    catchError((error: Response | any) => {
                        return observableThrowError(error);
                    }))
                    .subscribe({
                        next: () => {
                            this.progress.setVisible(false);
                            this.snackbar.open('User updated', null, {duration: 3000});
                            this.userForm.markAsPristine();
                            this.location.back();
                        }, error: error => {
                            this.progress.setVisible(false);
                            this.globalErrorMessage = error.toString();
                        }
                    });
            } else {
                this.userService.createUser(user).pipe(
                    catchError((error: Response | any) => {
                        return observableThrowError(error);
                    }))
                    .subscribe({
                        next: () => {
                            this.progress.setVisible(false);
                            this.snackbar.open('User saved', null, {duration: 3000});
                            this.userForm.markAsPristine();
                            this.location.back();
                        },
                        error: error => {
                            this.progress.setVisible(false);
                            this.globalErrorMessage = error.toString();
                        }
                    });
            }
        }
    }

    ngOnDestroy(): void {
        if (this.accountsSubscription) {
            this.accountsSubscription.unsubscribe();
        }
        if (this.facilitiesSubscription) {
            this.facilitiesSubscription.unsubscribe();
        }
        if (this.authStateSubscription) {
            this.authStateSubscription.unsubscribe();
        }
    }

    private fetchUser(userId: string) {
        this.progress.setVisible(true);
        this.userService.getUser(userId).subscribe(user => {
            this.user = user;
            this.breadcrumbs[2].title = 'Edit user: ' + user.email;
            if (user.admin) {
                if (this.isAdmin) {
                    this.userForm.get('role').setValue(1);
                } else {
                    this.snackbar.open('You don\'t have access to edit this user', null, {duration: 3000});
                    this.location.back();
                }
            } else if (user.operator) {
                if (this.isAdmin) {
                    this.userForm.get('role').setValue(2);
                } else {
                    this.snackbar.open('You don\'t have access to edit this user', null, {duration: 3000});
                    this.location.back();
                }
            } else {
                this.showFacilities = true;
                this.userForm.get('role').setValue(3);
            }
            this.userForm.patchValue(user);
            this.progress.setVisible(false);
            this.accountChanged({value: user.accountId});
        });
    }
}
